import React, { useContext, useState } from "react";
import { Button } from "components/Button/Button";
import modal from "components/Modal/ModalContent.module.css";
import { Form } from "components/Form/Form";
import { InputField } from "components/Input/InputField";
import { addToastNotification } from "shared/Toasts/toastNotificationStore";
import { SelectTheme } from "./SelectTheme";
import { useCreateFolderMutation } from "lib/types/graphql";
import { SessionContext } from "gui/session/session";
import { useActiveContext } from "shared/ContextSwitcher/store";
import { COLOR_OPTIONS } from "./themeOptions";

export function CreateFolderDialogContent({
  onDismiss,
  refetchFolders,
}: {
  onDismiss: () => void;
  refetchFolders: () => Promise<void>;
}) {
  const { session } = useContext(SessionContext);
  const activeContext = useActiveContext();
  const [formValues, setFormValues] = useState({
    name: "",
    description: "",
  });
  const { name, description } = formValues;

  const [selectedColor, setSelectedColor] = useState(
    "var(--feedback--accent--success)"
  );
  const [selectedIcon, setSelectedIcon] = useState("folder");

  const [
    createFolderMutation,
    createFolderMutationRes,
  ] = useCreateFolderMutation({
    context: { session },
    variables: {
      name,
      icon: selectedIcon,
      color:
        COLOR_OPTIONS.find((option) => option.value === selectedColor)?.label ??
        "green",
      description,
      team_id: activeContext?.type === "team" ? activeContext.id : undefined,
    },
  });

  const onCreate = () => {
    createFolderMutation()
      .then((res) => {
        setFormValues({
          name: "",
          description: "",
        });

        if (res.data?.create_folder.id !== undefined) {
          addToastNotification({
            level: "success",
            title: "New folder created.",
          });
        }
        refetchFolders();
        onDismiss();
      })
      .catch((err) => {
        setFormValues({
          name: "",
          description: "",
        });

        addToastNotification({
          level: "error",
          title: err.message,
        });
        onDismiss();
      });
  };

  return (
    <div className={modal.body}>
      <div className={modal.header}>
        <div className={modal.titleRow}>
          <h2>Create folder</h2>
        </div>

        <p className={modal.description}>
          Folder names are only visible to you and your team. Creating folders
          to organize your content can save you time, keep you focused, and
          facilitate collaboration.
        </p>
      </div>

      <Form size="M">
        <InputField
          size="M"
          style="contained"
          value={name}
          onChange={(event) =>
            setFormValues((prevFormValues) => ({
              ...prevFormValues,
              name: event.target.value,
            }))
          }
          label="Folder name"
          placeholder="Folder name..."
        />
        <InputField
          size="M"
          style="contained"
          value={description}
          onChange={(event) =>
            setFormValues((prevFormValues) => ({
              ...prevFormValues,
              description: event.target.value,
            }))
          }
          label="Description (optional)"
          placeholder="Folder description..."
          hint={`${description.length}/64 characters`}
          maxLength={64}
        />
        <SelectTheme
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
          selectedIcon={selectedIcon}
          setSelectedIcon={setSelectedIcon}
        />
      </Form>

      <div className={modal.buttons}>
        <Button size="M" theme="tertiary" onClick={onDismiss}>
          Cancel
        </Button>
        <Button
          size="M"
          theme="primary"
          onClick={onCreate}
          disabled={!name || createFolderMutationRes.loading}
        >
          Create folder
        </Button>
      </div>
    </div>
  );
}
